import { useEffect, useState } from 'react'
import { CloudOff, ChevronDown, ChevronUp, X, ExternalLink } from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import '@/design-system/shell.css'
import { useServiceStatus, useServiceStatusMessage, STATUS_PAGE_URLS } from '@/hooks/useServiceHealth'

/**
 * ServiceStatusBanner - Shows a friendly notification when services are experiencing issues.
 *
 * Features:
 * - Shows below header when services are degraded or down
 * - Collapsible for minimal distraction
 * - Dismissible per session
 * - Links to provider status page
 * - Auto-dismisses on recovery (with toast notification)
 */
export default function ServiceStatusBanner() {
  const {
    overall,
    affectedProvider,
    detectedAt,
    userDismissed,
    isCollapsed,
    dismissBanner,
    collapseBanner,
    expandBanner
  } = useServiceStatus()

  const message = useServiceStatusMessage()

  // Don't show if healthy or dismissed
  // Note: consecutiveFailures is only for passive detection resilience (axios errors),
  // not for blocking when the health API explicitly reports issues
  if (overall === 'healthy' || userDismissed) {
    return null
  }

  // Get the appropriate status page URL
  const statusPageUrl = affectedProvider ? STATUS_PAGE_URLS[affectedProvider] : null

  // Format detection time
  const detectionTime = detectedAt
    ? new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(detectedAt)
    : null

  /*
    Two severities in two hues, amber for degraded and blue for the rest,
    each with a matching icon, text, button and link colour: five pairs of
    Tailwind classes to say one thing. Both of them mean the same thing to a
    person looking at the top of their screen, which is that something is
    wrong and it is not their fault, so both take the one attention colour
    the system has. The words say which.
  */
  return (
    /* DsIsland takes no arbitrary props, so the live region is the element it
       wraps rather than the scope itself. */
    <DsIsland panel={false} ground={false} className="ds-outage">
      <div className="ds-outage__in" role="status" aria-live="polite">
        {/* Collapsed State */}
        {isCollapsed ? (
          <div className="ds-outage__row">
            <div className="ds-outage__what">
              <CloudOff aria-hidden="true" />
              <span className="ds-outage__say">
                {message?.headline || "We're experiencing some issues."}
              </span>
            </div>
            <div className="ds-outage__acts">
              <button
                onClick={expandBanner}
                className="ds-outage__btn"
                aria-label="Show details"
                title="Show details"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <button
                onClick={dismissBanner}
                className="ds-outage__btn"
                aria-label="Dismiss notification"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          /* Expanded State */
          <div className="ds-outage__open">
            {/* Header Row */}
            <div className="ds-outage__row">
              <div className="ds-outage__what">
                <CloudOff aria-hidden="true" />
                <div>
                  <p className="ds-outage__say">
                    {message?.headline || "We're experiencing some issues."}
                  </p>
                  <p className="ds-outage__more">
                    {message?.subtext || "Some features may be temporarily unavailable."}
                  </p>
                </div>
              </div>
              <div className="ds-outage__acts">
                <button
                  onClick={collapseBanner}
                  className="ds-outage__btn"
                  aria-label="Collapse notification"
                  title="Collapse"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={dismissBanner}
                  className="ds-outage__btn"
                  aria-label="Dismiss notification"
                  title="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Details Row */}
            <div className="ds-outage__facts">
              {detectionTime && (
                <span className="ds-outage__meta">
                  Detected: {detectionTime}
                </span>
              )}
              <span className="ds-outage__meta">
                Your data is safe.
              </span>
              {statusPageUrl && (
                <a
                  href={statusPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ds-outage__link"
                >
                  Check {affectedProvider ? capitalizeFirst(affectedProvider) : 'Provider'} Status
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </DsIsland>
  )
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * ServiceRecoveryToast - Shows a brief toast when services recover.
 * Renders as a portal and auto-dismisses after a few seconds.
 */
export function ServiceRecoveryToast() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const handleRecovery = () => {
      setIsVisible(true)
      setTimeout(() => setIsVisible(false), 5000)
    }

    window.addEventListener('service-status-recovered', handleRecovery)
    return () => window.removeEventListener('service-status-recovered', handleRecovery)
  }, [])

  if (!isVisible) return null

  return (
    <div
      className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2 fade-in duration-300"
      role="status"
      aria-live="polite"
    >
      <DsIsland panel={false} ground={false} className="ds-recovered">
        <div className="ds-recovered__dot" />
        <span className="ds-recovered__say">
          We're back! Everything's running smoothly again.
        </span>
      </DsIsland>
    </div>
  )
}
